import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OffersService } from '../../services/offers.service';
import { PartnersService } from '../../services/partners.service';
import { UsersService } from '../../services/users.service';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { Offer, EditOffer } from '../../interfaces/offers.interface';
import { PartnerGroup } from '../../interfaces/partners.interface';

@Component({
  selector: 'app-parnter-offer-edit',
  standalone: false,
  templateUrl: './parnter-offer-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./parnter-offer-edit.component.scss']
})
export class ParnterOfferEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() offerId?: number;
  @Input() partnerId?: number;
  @Output() offerSaved = new EventEmitter<Offer>();

  offerForm!: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;

  categories: PartnerGroup[] = [];
  imagesUrl = environment.ImagesUrl;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private offersService: OffersService,
    private partnersService: PartnersService,
    private usersService: UsersService,
    private snackBar: MatSnackBar,
  ) {
    this.initForm();
  }

  initForm(): void {
    this.offerForm = this.fb.group({
      title: ['', Validators.required],
      intro: [''],
      content: [''],
      discount: [''],
      file: [''],
      valid: [''],
      category_id: [null],
      is_active: [true]
    });
  }

  ngOnInit(): void {

    // Get user's partner_id if not provided
    if (!this.partnerId) {
      const user = this.usersService.getCurrentUser();
      if (user && user.partner_id && user.partner_id > 0) {
        this.partnerId = user.partner_id;
      }
    }

    // Load categories
    this.loadCategories();

    // Load offer data if editing existing offer
    if (this.offerId) {
      this.loadOffer();
    } 
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['offerId'] && changes['offerId'].currentValue) {
      this.loadOffer();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCategories(): void {
    this.partnersService.getPartnerGroup().subscribe({
      next: (groups) => {
        this.categories = groups;
      },
      error: () => {
        this.categories = [];
        this.snackBar.open('Nie udało się załadować kategorii', 'Zamknij', {
          duration: 3000,
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  loadOffer(): void {
    if (!this.offerId) {
      return;
    }
    
    this.loading = true;
    this.offersService.getData(this.offerId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (offer) => {
        // Decode HTML entities in content
        const decodedContent = this.decodeHtmlEntities(offer.content || '');
        
        this.offerForm.patchValue({
          title: offer.title || '',
          intro: offer.intro || '',
          content: decodedContent,
          discount: offer.discount || '',
          file: offer.file || '',
          valid: this.formatDateFromApi(offer.valid) || '',
          category_id: offer.category_id || null,
          is_active: offer.is_active !== undefined ? offer.is_active : true
        });
        
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading offer:', err);
        this.error = 'Błąd podczas ładowania oferty';
        this.loading = false;
        this.snackBar.open('Nie udało się załadować oferty', 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];


    this.offersService.uploadFile(file).subscribe({
      next: (res: any) => {
        if (res && res.file) {
          this.offerForm.patchValue({ file: res.file });
        }
      },
      error: () => {
        alert('Błąd podczas przesyłania pliku.');
      }
    });
  }

  getOfferImageUrl(filename: string): string {
    return `${this.imagesUrl}/offer/${filename}`;
  }

  // Decode HTML entities
  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  // Date formatting methods
  private formatDateFromApi(apiDate: string): Date | null {
    if (!apiDate) return null;
    // API format: "2013-12-23T09:39:43" - extract date part and create Date object
    const datePart = apiDate.split('T')[0]; // "2013-12-23"
    return new Date(datePart);
  }

  private formatDateToApi(date: Date | string | null): string {
    if (!date) return '';
    
    // If it's already a string in the correct format, return as is
    if (typeof date === 'string') {
      // Check if it's already in dd.mm.yyyy format
      if (date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        // Convert dd.mm.yyyy to yyyy-mm-dd for API
        const parts = date.split('.');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return date;
    }
    
    // If it's a Date object, format as yyyy-mm-dd
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onSubmit(): void {
    if (this.offerForm.invalid || !this.partnerId) {
      return;
    }

    this.saving = true;
    this.error = null;

    const formData = this.offerForm.value;
    
    // Add partner_id to the form data
    const offerData: EditOffer = {
      title: formData.title || '',
      intro: formData.intro || '',
      content: formData.content || '',
      discount: formData.discount || '',
      file: formData.file || '',
      valid: this.formatDateToApi(formData.valid) || '',
      category_id: formData.category_id || null,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
      partner_id: this.partnerId
    };

    const request = this.offerId 
      ? this.offersService.updateOffer(this.offerId, offerData)
      : this.offersService.createOffer(offerData);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (updatedOffer) => {
        this.saving = false;
        const message = this.offerId ? 'Oferta została zaktualizowana' : 'Oferta została utworzona';
        this.snackBar.open(message, 'Zamknij', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        
        // If it was a new offer, set the ID for future edits
        if (!this.offerId) {
          this.offerId = updatedOffer.id;
        }

        // Emit the saved offer
        this.offerSaved.emit(updatedOffer);
      },
      error: (err) => {
        console.error('Error saving offer:', err);
        this.error = 'Błąd podczas zapisywania oferty';
        this.saving = false;
        const message = this.offerId ? 'Nie udało się zaktualizować oferty' : 'Nie udało się utworzyć oferty';
        this.snackBar.open(message, 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }
}
